import api from './api';

export const authService = {
  register: async (userData) => {
    const response = await api.post('/auth/register', userData);
    return response.data;
  },

  studentLogin: async (collegeEmail, password) => {
    const response = await api.post('/auth/login/student', { collegeEmail, password });
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }
    return response.data;
  },

  staffLogin: async (collegeEmail, password) => {
    const response = await api.post('/auth/login/staff', { collegeEmail, password });
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }
    return response.data;
  },

  sendLoginOTP: async (collegeEmail) => {
    const response = await api.post('/auth/login/send-otp', { collegeEmail });
    return response.data;
  },

  verifyOTPAndLogin: async (collegeEmail, otpCode) => {
    const response = await api.post('/auth/login/verify-otp', { collegeEmail, otpCode });
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }
    return response.data;
  },

  resendOTP: async (collegeEmail, purpose) => {
    const response = await api.post('/auth/resend-otp', { collegeEmail, purpose });
    return response.data;
  },

  sendForgotPasswordOTP: async (collegeEmail) => {
    const response = await api.post('/auth/forgot-password/send-otp', { collegeEmail });
    return response.data;
  },

  resetPassword: async (collegeEmail, otpCode, newPassword) => {
    const response = await api.post('/auth/forgot-password/reset', { collegeEmail, otpCode, newPassword });
    return response.data;
  },

  verifyEmail: async (token) => {
    const response = await api.post('/auth/verify-email', { token });
    return response.data;
  },

  resendVerification: async (collegeEmail) => {
    const response = await api.post('/auth/resend-verification', { collegeEmail });
    return response.data;
  },

  getCurrentUser: async () => {
    const response = await api.get('/auth/me');
    return response.data.user;
  },

  logout: async () => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      // Ignore backend errors during logout - just clear local data
      console.warn('Logout API call failed, clearing local data anyway');
    }
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  },

  changePassword: async (oldPassword, newPassword) => {
    const response = await api.post('/auth/change-password', { oldPassword, newPassword });
    return response.data;
  }
};

export const nocService = {
  createNOCRequest: async (nocData) => {
    const response = await api.post('/noc', nocData);
    return response.data;
  },

  getNOCRequests: async (params) => {
    const response = await api.get('/noc', { params });
    return response.data;
  },

  getPendingNOCRequests: async (params) => {
    const response = await api.get('/noc/status/pending', { params });
    return response.data;
  },

  getApprovedNOCRequests: async (params) => {
    const response = await api.get('/noc/status/approved', { params });
    return response.data;
  },

  getRejectedNOCRequests: async (params) => {
    const response = await api.get('/noc/status/rejected', { params });
    return response.data;
  },

  getNOCRequestById: async (id) => {
    const response = await api.get(`/noc/${id}`);
    return response.data;
  },

  processNOCApproval: async (id, status, remarks) => {
    const response = await api.post(`/noc/${id}/approve`, { status, remarks });
    return response.data;
  },

  bulkProcessNOC: async (nocIds, status, remarks) => {
    const response = await api.post('/noc/bulk/process', { nocIds, status, remarks });
    return response.data;
  },

  getNOCStatistics: async () => {
    const response = await api.get('/noc/statistics');
    return response.data;
  },

  searchNOCByUSN: async (usn) => {
    const response = await api.get('/noc/search', { params: { usn } });
    return response.data;
  },

  // Super Admin methods
  getSuperAdminStats: async () => {
    const response = await api.get('/noc/admin/stats');
    return response.data;
  },

  getAllNOCsForSuperAdmin: async (params) => {
    const response = await api.get('/noc/admin/all-nocs', { params });
    return response.data;
  },

  superAdminApproveNOC: async (id, data) => {
    const response = await api.post(`/noc/admin/${id}/modify`, data);
    return response.data;
  },

  bulkApproveByUSN: async (formData) => {
    const response = await api.post('/noc/admin/bulk-approve', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }
};

export const userService = {
  getProfile: async () => {
    const response = await api.get('/users/profile');
    return response.data.user;
  },

  updateProfile: async (userData) => {
    const response = await api.put('/users/profile', userData);
    return response.data;
  }
};

export const updatesService = {
  reactToUpdate: async (id, payload) => {
    const response = await api.post(`/updates/${id}/react`, payload);
    return response.data;
  },

  getUpdates: async () => {
    const response = await api.get('/updates');
    return response.data;
  },

  getArchivedUpdates: async () => {
    const response = await api.get('/updates/archived');
    return response.data;
  },

  getManageUpdates: async () => {
    const response = await api.get('/updates/manage');
    return response.data;
  },

  createUpdate: async (payload) => {
    const isFormData = typeof FormData !== 'undefined' && payload instanceof FormData;
    const response = await api.post('/updates', payload, isFormData ? {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    } : undefined);
    return response.data;
  },

  updateUpdate: async (id, payload) => {
    const isFormData = typeof FormData !== 'undefined' && payload instanceof FormData;
    const response = await api.put(`/updates/${id}`, payload, isFormData ? {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    } : undefined);
    return response.data;
  },

  deleteUpdate: async (id) => {
    const response = await api.delete(`/updates/${id}`);
    return response.data;
  },

  archiveUpdate: async (id) => {
    const response = await api.patch(`/updates/${id}/archive`);
    return response.data;
  },
};

export const timetableService = {
  // Get timetable entries (public)
  getTimetable: async (filters = {}) => {
    const response = await api.get('/timetable', { params: filters });
    return response.data;
  },

  // Get all subjects (staff only)
  getSubjects: async (filters = {}) => {
    const response = await api.get('/timetable/subjects', { params: filters });
    return response.data;
  },

  // Create a new subject (staff only)
  createSubject: async (subjectData) => {
    const response = await api.post('/timetable/subjects', subjectData);
    return response.data;
  },

  // Update subject code/name (staff only)
  updateSubject: async (id, subjectData) => {
    const response = await api.put(`/timetable/subjects/${id}`, subjectData);
    return response.data;
  },

  // Delete (deactivate) subject (staff only)
  deleteSubject: async (id) => {
    const response = await api.delete(`/timetable/subjects/${id}`);
    return response.data;
  },

  // Create a new timetable entry (staff only)
  createTimetableEntry: async (entryData) => {
    const response = await api.post('/timetable/entries', entryData);
    return response.data;
  },

  // Update a timetable entry (staff only)
  updateTimetableEntry: async (id, entryData) => {
    const response = await api.put(`/timetable/entries/${id}`, entryData);
    return response.data;
  },

  // Delete a timetable entry (staff only)
  deleteTimetableEntry: async (id) => {
    const response = await api.delete(`/timetable/entries/${id}`);
    return response.data;
  },

  // Bulk create timetable entries (staff only)
  bulkCreateTimetable: async (entries) => {
    const response = await api.post('/timetable/bulk', { entries });
    return response.data;
  }
};

export const academicsService = {
  // Get all academic events
  getEvents: async (filters = {}) => {
    const response = await api.get('/academics/events', { params: filters });
    return response.data;
  },

  // Get upcoming events (next 7 days)
  getUpcomingEvents: async () => {
    const response = await api.get('/academics/events/upcoming');
    return response.data;
  },

  // Get single event by ID
  getEventById: async (id) => {
    const response = await api.get(`/academics/events/${id}`);
    return response.data;
  },

  // Create new event (staff only)
  createEvent: async (eventData) => {
    const response = await api.post('/academics/events', eventData);
    return response.data;
  },

  // Update event (staff only)
  updateEvent: async (id, eventData) => {
    const response = await api.put(`/academics/events/${id}`, eventData);
    return response.data;
  },

  // Delete event (staff only)
  deleteEvent: async (id) => {
    const response = await api.delete(`/academics/events/${id}`);
    return response.data;
  },

  uploadEventVenuePdf: async (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/academics/events/${id}/venue-pdf`, formData);
    return response.data;
  },

  downloadEventVenuePdf: async (id) => {
    const response = await api.get(`/academics/events/${id}/venue-pdf`, {
      responseType: 'blob'
    });
    return response.data;
  }
};

export const gradesService = {
  getStudentsForGrades: async (params) => {
    const response = await api.get('/grades/students', { params });
    return response.data;
  },

  getSubjectsForGrades: async (params) => {
    const response = await api.get('/grades/subjects', { params });
    return response.data;
  },

  updateGrades: async (payload) => {
    const response = await api.post('/grades/update', payload);
    return response.data;
  },

  getMyGrades: async (params = {}) => {
    const response = await api.get('/grades/my-grades', { params });
    return response.data;
  }
};

export const staffService = {
  // Get all staff members
  getAllStaff: async (filters = {}) => {
    const response = await api.get('/staff', { params: filters });
    return response.data;
  },

  // Get staff statistics
  getStatistics: async () => {
    const response = await api.get('/staff/statistics');
    return response.data;
  },

  // Get staff member by ID
  getStaffById: async (id) => {
    const response = await api.get(`/staff/${id}`);
    return response.data;
  },

  // Create new staff member
  createStaff: async (staffData) => {
    const response = await api.post('/staff', staffData);
    return response.data;
  },

  // Update staff member
  updateStaff: async (id, staffData) => {
    const response = await api.put(`/staff/${id}`, staffData);
    return response.data;
  },

  // Delete staff member (soft delete)
  deleteStaff: async (id) => {
    const response = await api.delete(`/staff/${id}`);
    return response.data;
  },

  // Reset staff password
  resetPassword: async (id, newPassword) => {
    const response = await api.post(`/staff/${id}/reset-password`, { newPassword });
    return response.data;
  }
};

export const departmentService = {
  // Get all departments
  getAllDepartments: async () => {
    const response = await api.get('/departments');
    return response.data;
  },

  // Get department by ID
  getDepartmentById: async (id) => {
    const response = await api.get(`/departments/${id}`);
    return response.data;
  },

  // Create new department (SUPER_ADMIN only)
  createDepartment: async (departmentData) => {
    const response = await api.post('/departments', departmentData);
    return response.data;
  },

  // Update department (SUPER_ADMIN only)
  updateDepartment: async (id, departmentData) => {
    const response = await api.put(`/departments/${id}`, departmentData);
    return response.data;
  },

  // Delete department (SUPER_ADMIN only)
  deleteDepartment: async (id) => {
    const response = await api.delete(`/departments/${id}`);
    return response.data;
  }
};

/**
 * TEMPORARY FEATURE - Exam Details Service
 * 
 * TO REMOVE: Delete this entire export block and remove the import from Dashboard.js
 */
export const examDetailsService = {
  // Get exam details by USN
  getExamDetailsByUSN: async (usn) => {
    const response = await api.get(`/exam-details/${usn}`);
    return response.data;
  }
};
